import type { AbstractEngine, Color4, GlowLayer, Mesh, Scene, ShadowGenerator, TransformNode } from "@babylonjs/core";
import type { Track } from "@rivalwatch/shared";

export interface Owned {
  dispose(): void;
}

/**
 * How a map looks: sky, light and ink, plus whatever it adds around its GLB. Each map's look lives
 * in its own folder (`maps/<id>/`), like a hero's presentation; generic code never names a map.
 */
export interface MapLook {
  /** Sky colour at the zenith; with `horizon`, a gradient dome fades down to it. */
  sky: { top: string; horizon?: string };
  fog: { color: string; density: number };
  hemi: { intensity: number; sky: string; ground: string; dir: [number, number, number] };
  /** `dir` is the way the light travels (game axes, y up). */
  sun: { dir: [number, number, number]; intensity: number; color: string };
  /** 0 = black shadows, 1 = none. */
  shadowDarkness: number;
  ink: { color: string; width: number };
  glow: number;
  /**
   * Adds the map's scenery around the loaded GLB (backdrops, clouds, painted panels). On a map
   * built in stages, scenery that belongs to one stage is tagged with `onStage`.
   */
  dress?(ctx: DressContext): Owned[];
  /** Control maps built in stages: each stage's own sky and light, over the look's. */
  stages?: StageLook[];
  /** Convoy: dresses the payload's route (default: a ballasted bed, sleepers and rails). */
  track?(ctx: TrackContext): void;
  /** Convoy: dresses the payload (default: a flatbed carrying a glowing core). */
  payload?(ctx: PayloadContext): PayloadView;
  /**
   * The landing page's aerial view: a slow orbit around the map or, with `sweep`, a slow swing of
   * ±sweep radians about `alpha` that keeps one composition (and the edge of the world out of shot).
   */
  showcase?: { target: [number, number, number]; radius: number; alpha: number; beta: number; fov?: number; sweep?: number; stage?: number };
}

/** What a stage (Domination sub-map) may set for itself: its sky, air and light. */
export type StageLook = Partial<Pick<MapLook, "sky" | "fog" | "hemi" | "sun" | "shadowDarkness" | "glow">>;

/** Marks scenery as one stage's: it's only drawn while that stage is in play. */
export function onStage<T extends { metadata: unknown }>(node: T, stage: number): T {
  node.metadata = { ...(node.metadata as object | null), stage };
  return node;
}

/** The stage a node belongs to (a map part's glTF extras, or scenery tagged with onStage). */
export function stageOf(node: { metadata: unknown }): number | undefined {
  const md = node.metadata as { stage?: number; gltf?: { extras?: { stage?: number } } } | null;
  const k = md?.stage ?? md?.gltf?.extras?.stage;
  return typeof k === "number" ? k : undefined;
}

export interface DressContext {
  engine: AbstractEngine;
  scene: Scene;
  shadows: ShadowGenerator;
  glow: GlowLayer;
  ink: Color4;
  /** How far the map reaches from the origin horizontally (m). */
  extent: number;
  /** The GLB's MURAL_* and SIGN_* panels: UV'd quads whose extras say what to paint. */
  panels: Mesh[];
}

export interface TrackContext {
  scene: Scene;
  track: Track;
  ink: Color4;
  /** Keeps something for disposal at the next map swap. */
  own<T extends Owned>(m: T): T;
}

export interface PayloadContext extends TrackContext {
  shadows: ShadowGenerator;
}

/** A payload's dressing. The game moves `root` along the track, pitched to its slope. */
export interface PayloadView {
  root: TransformNode;
  /** Meshes that take the payload's state colour. */
  glows: Mesh[];
  /** The push-radius ring on the ground. */
  ring: Mesh;
  /** The marker that floats over the payload. */
  orb: Mesh;
  /** Where the marker floats (payload space). */
  orbHeight: number;
  /** Per frame: seconds since load, and whether it's moving. */
  update?(t: number, moving: boolean): void;
}
